import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Form, Table, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createOrder } from '../../../services/OrdersService';
import { getProducts } from '../../../services/ProductsService';
import { SELECTABLE_PAYMENT_METHODS } from '../../../utils/paymentMethodOptions';

export default function CreateOrder() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orderDate, setOrderDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [deliveryDate, setDeliveryDate] = useState('');
    const [buyerName, setBuyerName] = useState('');
    const [paymentStatus, setPaymentStatus] = useState(''); // '' | 'paid' | 'unpaid' | 'partial' — mandatory
    const [paymentMethod, setPaymentMethod] = useState(''); // optional: from Payment page list
    const [amountPaidPartial, setAmountPaidPartial] = useState(''); // for "Part of it": amount paid
    const [notes, setNotes] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [streetName, setStreetName] = useState('');
    const [houseNumber, setHouseNumber] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [items, setItems] = useState([]); // { product_id, product_name, quantity, discount_percent, notes }
    const [addingProductId, setAddingProductId] = useState('');
    const [addingQty, setAddingQty] = useState('1');
    const [addingDiscount, setAddingDiscount] = useState(0);
    const [addingNote, setAddingNote] = useState('');

    useEffect(() => {
        getProducts()
            .then(res => setProducts(res.data || []))
            .catch(() => toast.error(t('createOrder.errors.loadProducts')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getProductDisplayName = useCallback((p) =>
        (i18n.language.startsWith('en') && p.name_en) ? p.name_en : (p.name_nl || p.name),
    [i18n.language]);

    const productOptions = useMemo(
        () =>
            products
                .filter(p => !p.is_delivery && !p.is_rd)
                .map((p) => {
                    const price = p.price_incl_tax ?? p.price ?? 0;
                    return {
                        value: p.id,
                        label: `${getProductDisplayName(p)} (${price.toFixed(2)} each)`,
                    };
                }),
        [products, getProductDisplayName]
    );

    const productSelectValue = useMemo(
        () => productOptions.find((o) => o.value === Number(addingProductId)) || null,
        [productOptions, addingProductId]
    );

    const filterOption = (option, rawInput) => {
        const q = rawInput.trim().toLowerCase();
        if (!q) return true;
        const tokens = q.split(/\s+/).filter(Boolean);
        const label = (option.label || '').toLowerCase();
        // allow matching by any word, including second/third word
        return tokens.every((t) => label.includes(t));
    };

    const isTraceableAndOutOfStock = (p) =>
        Boolean(p?.traceable) && (parseFloat(p.quantity_available) || 0) <= 0;

    const addItem = () => {
        const pid = parseInt(addingProductId);
        const qty = parseFloat(addingQty);
        if (!pid || qty <= 0) {
            toast.warning(t('createOrder.errors.selectProductAndQty'));
            return;
        }
        const prod = products.find(p => p.id === pid);
        const existing = items.find(i => i.product_id === pid);
        if (existing) {
            setItems(items.map(i => i.product_id === pid ? { ...i, quantity: i.quantity + qty } : i));
        } else {
            setItems([...items, { product_id: pid, product_name: prod?.name_nl || prod?.name || '', product_name_nl: prod?.name_nl || prod?.name || '', product_name_en: prod?.name_en || '', quantity: qty, discount_percent: addingDiscount, notes: addingNote.trim() || null }]);
        }
        setAddingProductId('');
        setAddingQty('1');
        setAddingDiscount(0);
        setAddingNote('');
    };

    const removeItem = (productId) => setItems(items.filter(i => i.product_id !== productId));

    const getTotal = () => {
        return items.reduce((sum, it) => {
            const prod = products.find(p => p.id === it.product_id);
            const price = prod?.price_incl_tax ?? prod?.price ?? 0;
            const discount = Math.min(100, Math.max(0, Number(it.discount_percent) || 0));
            return sum + it.quantity * price * (100 - discount) / 100;
        }, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!orderDate?.trim()) {
            toast.warning(t('createOrder.errors.orderDateRequired'));
            return;
        }
        if (!deliveryDate?.trim()) {
            toast.warning(t('createOrder.errors.deliveryDateRequired'));
            return;
        }
        if (!buyerName?.trim()) {
            toast.warning(t('createOrder.errors.buyerNameRequired'));
            return;
        }
        if (items.length === 0) {
            toast.warning(t('createOrder.errors.addAtLeastOneItem'));
            return;
        }
        if (!['paid', 'unpaid', 'partial'].includes(paymentStatus)) {
            toast.warning(t('createOrder.errors.selectPaidStatus'));
            return;
        }
        const total = getTotal();
        if (paymentStatus === 'partial') {
            const paid = parseFloat(amountPaidPartial);
            if (isNaN(paid) || paid < 0 || paid > total) {
                toast.warning(t('createOrder.errors.amountPaidRange', { total: total.toFixed(2) }));
                return;
            }
        }
        const outOfStockTraceable = items
            .map((it) => products.find((p) => p.id === it.product_id))
            .filter((p) => p && isTraceableAndOutOfStock(p));
        if (outOfStockTraceable.length > 0) {
            const names = outOfStockTraceable.map((p) => p.name).filter(Boolean).join(', ');
            toast.warning(
                t('createOrder.errors.outOfStockWarning', { names }),
                { autoClose: 6000 }
            );
        }
        const payload = {
            order_date: orderDate.trim(),
            delivery_date: deliveryDate.trim(),
            buyer_name: buyerName.trim(),
            payment_status: paymentStatus,
            payment_method: paymentMethod.trim() || null,
            notes: notes.trim() || null,
            customer_email: customerEmail.trim() || null,
            customer_phone: customerPhone.trim() || null,
            street_name: streetName.trim() || null,
            house_number: houseNumber.trim() || null,
            zip_code: zipCode.trim() || null,
            items: items.map(i => ({
                product_id: i.product_id,
                quantity: i.quantity,
                discount_percent: Math.min(100, Math.max(0, Number(i.discount_percent) || 0)),
                notes: i.notes?.trim() || null,
            })),
        };
        if (paymentStatus === 'partial') {
            payload.amount_paid = parseFloat(amountPaidPartial);
        }
        try {
            await createOrder(payload);
            toast.success(t('createOrder.orderCreated'));
            navigate('/orders');
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || err.message || t('createOrder.errors.createFailed');
            toast.error(msg);
        }
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h4 className="heading mb-0">{t('createOrder.title')}</h4>
                                <span className="text-muted">{t('createOrder.subtitle')}</span>
                            </div>
                            <Button variant="light" size="sm" onClick={() => navigate('/orders')}>
                                <i className="fa-solid fa-arrow-left me-1" /> {t('createOrder.backButton')}
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Row className="mb-4">
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.orderDate')} <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={orderDate}
                                                onChange={(e) => setOrderDate(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.deliveryDate')} <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={deliveryDate}
                                                onChange={(e) => setDeliveryDate(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.buyerName')} <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                value={buyerName}
                                                onChange={(e) => setBuyerName(e.target.value)}
                                                placeholder={t('createOrder.form.buyerNamePlaceholder')}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.customerEmail')}</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={customerEmail}
                                                onChange={(e) => setCustomerEmail(e.target.value)}
                                                placeholder={t('createOrder.form.customerEmailPlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.customerPhone')}</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                value={customerPhone}
                                                onChange={(e) => setCustomerPhone(e.target.value)}
                                                placeholder={t('createOrder.form.customerPhonePlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.isPaid')} <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                value={paymentStatus}
                                                onChange={(e) => {
                                                    setPaymentStatus(e.target.value);
                                                    if (e.target.value !== 'partial') setAmountPaidPartial('');
                                                }}
                                                required
                                            >
                                                <option value="">{t('createOrder.form.selectOption')}</option>
                                                <option value="paid">{t('common.yes')}</option>
                                                <option value="unpaid">{t('common.no')}</option>
                                                <option value="partial">{t('tasksPage.paymentPartial')}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3" controlId="create-order-payment-method">
                                            <Form.Label>{t('createOrder.form.paymentMethod')}</Form.Label>
                                            <Form.Select
                                                value={paymentMethod}
                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                            >
                                                <option value="">{t('createOrder.form.selectOptional')}</option>
                                                {SELECTABLE_PAYMENT_METHODS.map((m) => (
                                                    <option key={m.value} value={m.value}>{m.labelKey ? t(m.labelKey) : m.value}</option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                {t('createOrder.form.paymentMethodHelp')}
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    {paymentStatus === 'partial' && (
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.amountPaid')} <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max={getTotal()}
                                                    value={amountPaidPartial}
                                                    onChange={(e) => setAmountPaidPartial(e.target.value)}
                                                    placeholder={`0 - ${getTotal().toFixed(2)}`}
                                                    required={paymentStatus === 'partial'}
                                                />
                                                <Form.Text className="text-muted">
                                                    {t('createOrder.form.remaining', { amount: (getTotal() - (parseFloat(amountPaidPartial) || 0)).toFixed(2) })}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    )}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.notes')}</Form.Label>
                                            <Form.Control
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder={t('createOrder.form.notesPlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <h6 className="mb-2 text-muted">{t('createOrder.form.deliveryAddressHeading')}</h6>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.streetName')}</Form.Label>
                                            <Form.Control
                                                value={streetName}
                                                onChange={(e) => setStreetName(e.target.value)}
                                                placeholder={t('createOrder.form.streetNamePlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.houseNumber')}</Form.Label>
                                            <Form.Control
                                                value={houseNumber}
                                                onChange={(e) => setHouseNumber(e.target.value)}
                                                placeholder={t('createOrder.form.houseNumberPlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('createOrder.form.zipCode')}</Form.Label>
                                            <Form.Control
                                                value={zipCode}
                                                onChange={(e) => setZipCode(e.target.value)}
                                                placeholder={t('createOrder.form.zipCodePlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <h6 className="mb-2">{t('createOrder.addItems.heading')}</h6>
                                <Row className="mb-3 align-items-end">
                                    <Col md={4}>
                                        <Select
                                            classNamePrefix="react-select"
                                            placeholder={t('createOrder.addItems.selectProduct')}
                                            isClearable
                                            isSearchable
                                            options={productOptions}
                                            value={productSelectValue}
                                            onChange={(opt) => setAddingProductId(opt?.value ? String(opt.value) : '')}
                                            filterOption={filterOption}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            placeholder={t('createOrder.addItems.qtyPlaceholder')}
                                            value={addingQty}
                                            onChange={(e) => setAddingQty(e.target.value)}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-0">
                                            <Form.Label className="small">{t('createOrder.addItems.discountLabel')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="1"
                                                value={addingDiscount}
                                                onChange={(e) => setAddingDiscount(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                                placeholder="0"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={2}>
                                        <Form.Group className="mb-0">
                                            <Form.Label className="small">{t('createOrder.addItems.itemNoteLabel')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={addingNote}
                                                onChange={(e) => setAddingNote(e.target.value)}
                                                placeholder={t('createOrder.addItems.itemNotePlaceholder')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} sm={6} md={2} className="d-flex align-items-end">
                                        <Button variant="primary" type="button" onClick={addItem} className="w-100">
                                            <i className="fa-solid fa-plus me-1" /> {t('createOrder.addItems.addButton')}
                                        </Button>
                                    </Col>
                                </Row>

                                <Table responsive bordered>
                                    <thead>
                                        <tr>
                                            <th>{t('createOrder.table.product')}</th>
                                            <th>{t('createOrder.table.quantity')}</th>
                                            <th>{t('createOrder.table.unitPrice')}</th>
                                            <th>{t('createOrder.table.total')}</th>
                                            <th className="text-center">{t('createOrder.table.discount')}</th>
                                            <th>{t('createOrder.table.note')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center text-muted py-4">
                                                    {t('createOrder.table.noItems')}
                                                </td>
                                            </tr>
                                        ) : (
                                            items.map((it) => {
                                                const prod = products.find(p => p.id === it.product_id);
                                                const price = prod?.price_incl_tax ?? prod?.price ?? 0;
                                                const outOfStock = prod && isTraceableAndOutOfStock(prod);
                                                return (
                                                    <tr key={it.product_id} className={outOfStock ? 'table-warning' : ''}>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span>{(i18n.language.startsWith('en') && it.product_name_en) ? it.product_name_en : it.product_name}</span>
                                                                {outOfStock && (
                                                                    <span className="small text-danger fw-semibold mt-1 d-inline-flex align-items-center">
                                                                        <i className="fa-solid fa-triangle-exclamation me-1" aria-hidden />
                                                                        {t('createOrder.table.outOfStock')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>{it.quantity}</td>
                                                        <td>{price.toFixed(2)}</td>
                                                        <td>{(() => {
                                                            const d = Math.min(100, Math.max(0, Number(it.discount_percent) || 0));
                                                            return (it.quantity * price * (100 - d) / 100).toFixed(2);
                                                        })()}</td>
                                                        <td>
                                                            <Form.Control
                                                                type="number"
                                                                size="sm"
                                                                min="0"
                                                                max="100"
                                                                step="1"
                                                                value={Math.min(100, Math.max(0, Number(it.discount_percent) || 0))}
                                                                onChange={(e) => {
                                                                    const v = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                                                    setItems(items.map(i => i.product_id === it.product_id ? { ...i, discount_percent: v } : i));
                                                                }}
                                                                style={{ maxWidth: 80 }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                type="text"
                                                                size="sm"
                                                                value={it.notes || ''}
                                                                onChange={(e) => {
                                                                    setItems(items.map(i => i.product_id === it.product_id ? { ...i, notes: e.target.value } : i));
                                                                }}
                                                                placeholder={t('createOrder.table.notePlaceholder')}
                                                                style={{ minWidth: 150 }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Button variant="light" size="sm" className="text-danger" onClick={() => removeItem(it.product_id)}>
                                                                <i className="fa-solid fa-trash" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </Table>

                                {items.length > 0 && (
                                    <div className="mt-3 pt-3 border-top">
                                        <h5>{t('createOrder.totalLabel')} <span className="text-primary">{getTotal().toFixed(2)}</span></h5>
                                    </div>
                                )}

                                <div className="mt-4">
                                    <Button variant="secondary" className="me-2" onClick={() => navigate('/orders')}>
                                        {t('common.cancel')}
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={
                                                items.length === 0 || !orderDate?.trim() || !deliveryDate?.trim() || !buyerName?.trim() ||
                                                !['paid', 'unpaid', 'partial'].includes(paymentStatus) ||
                                                (paymentStatus === 'partial' && (isNaN(parseFloat(amountPaidPartial)) || parseFloat(amountPaidPartial) < 0 || parseFloat(amountPaidPartial) > getTotal()))
                                            }>
                                        {t('createOrder.createButton')}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}
